import {
  Box,
  DatePickerField,
  Flex,
  Form,
  SubmitButton,
  TextInputField,
} from '@/components';
import { useForm } from '@/hooks';
import { DayjsSchema } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import { type FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useGroupListSearchQueryStates } from '../_hooks/search-query-states';

const GroupSearchFormSchema = z
  .object({
    name: z.string().nullish(),
    fiscalYear: DayjsSchema().nullable(),
  })
  .partial();

type FormValues = z.infer<typeof GroupSearchFormSchema>;

export const GroupSearchForm: FC = () => {
  const { t } = useTranslation();
  const { searchQueryStates, setSearchQueryStates } =
    useGroupListSearchQueryStates();

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
  } = useForm<FormValues>({
    defaultValues: {
      name: searchQueryStates.name,
      fiscalYear: searchQueryStates.fiscalYear
        ? dayjs().year(Number(searchQueryStates.fiscalYear))
        : undefined,
    },
    resolver: zodResolver(GroupSearchFormSchema),
  });

  const onSubmit = useCallback(
    async (values: FormValues) => {
      setSearchQueryStates((v) => ({
        ...v,
        fiscalYear: values.fiscalYear?.year().toString() ?? null,
        name: values.name ?? null,
        page: 1,
      }));
    },
    [setSearchQueryStates],
  );

  return (
    <Form
      leavePageAlertCondition={null}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <Box marginBottom={24}>
        <Flex gap={16} align="flex-end">
          <DatePickerField
            name="fiscalYear"
            label={t('fiscal-year')}
            control={control}
            errors={errors}
            picker="year"
            wrapperStyle={{ marginBottom: 0 }}
          />
          <TextInputField
            name="name"
            autocompleteKey="group.name"
            label={t('name')}
            control={control}
            errors={errors}
            width={300}
            marginBottom={0}
          />
          <SubmitButton text={t('search')} loading={isSubmitting} />
        </Flex>
      </Box>
    </Form>
  );
};
